import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Box, Button, Drawer, Typography } from '@mui/material';

import {
  IResourceFile,
  adminBrowseResources,
  getResourcesState,
} from '../../../../store/ResourcesSlice';
import { useAppSelector } from '../../../../hooks';
import ResourcesExplorerMini from '../../../../components/ResourcesExplorerMini';
import {
  IAdminAddSubTopicResouces,
  adminAddSubTopicResources,
  getSubTopicState,
} from '../../../../store/SubTopicSlice';

interface AddNewResourceModalProps {
  open: boolean;
  onClose: () => void;
}

const resourcesExplorerContainerStyles: React.CSSProperties = {
  minHeight: 'calc(100vh - 62px - 24px - 40px)',
  maxHeight: 'calc(100vh - 62px - 24px - 40px)',
  overflowY: 'auto',
  padding: '0 15px',
};

const AddNewResourceModal: React.FC<AddNewResourceModalProps> = ({
  open,
  onClose,
}) => {
  const dispatch = useDispatch();
  const urlParams = useParams();

  // Store
  const resourcesState = useAppSelector(getResourcesState);
  const subTopicState = useAppSelector(getSubTopicState);

  // State
  const [resourceExplorerHistory, setResourceExplorerHistory] = useState(['/']);
  const [selectedResources, setSelectedResources] = useState<IResourceFile[]>(
    []
  );

  const subTopicData = subTopicState.subTopic;

  let hiddenFiles: string[] = [];

  if (subTopicData && subTopicData.resources && subTopicData.resources.length) {
    hiddenFiles = subTopicData.resources.map((item) => item.id);
  }

  useEffect(() => {
    if (open) {
      dispatch(
        adminBrowseResources({
          resourceKey: '/',
        })
      );
    } else {
      setResourceExplorerHistory(['/']);
      setSelectedResources([]);
      dispatch(
        adminBrowseResources({
          resourceKey: '/',
        })
      );
    }
  }, [open]);

  const handleResourceExplorerFolderChange = (folder: IResourceFile) => {
    dispatch(
      adminBrowseResources({
        resourceKey: folder.id,
      })
    );

    setResourceExplorerHistory((previousResourceExplorerHistory) => {
      return [...previousResourceExplorerHistory, folder.id];
    });
  };

  const handleResourceExplorerBackNavigation = () => {
    const index = resourceExplorerHistory.length - 1;
    const newHistory = resourceExplorerHistory.filter((h, i) => i !== index);

    dispatch(
      adminBrowseResources({
        resourceKey: resourceExplorerHistory[index - 1],
      })
    );

    setResourceExplorerHistory(newHistory);
  };

  const handleResourceExplorerFileClick = (file: IResourceFile) => {
    setSelectedResources([file]);
  };

  const handleResourceExplorerSelectedFileDelete = (file: IResourceFile) => {
    setSelectedResources((ps) => ps.filter((f) => f.id !== file.id));
  };

  const handleSubmit = () => {
    if (urlParams.subTopicId) {
      const apiData: IAdminAddSubTopicResouces = {
        subTopicId: urlParams.subTopicId,
        resources: selectedResources,
      };

      dispatch(adminAddSubTopicResources(apiData));
    }

    onClose();
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      anchor="right"
      PaperProps={{ sx: { minWidth: '350px', maxWidth: '350px' } }}
    >
      <Box role="presentation">
        <div style={{ padding: '15px' }}>
          <Typography variant="h6" fontWeight={600}>
            Upload a new document
          </Typography>
        </div>
        <div style={resourcesExplorerContainerStyles}>
          <ResourcesExplorerMini
            selectedResources={selectedResources}
            isLoading={resourcesState.status === 'loading'}
            history={resourceExplorerHistory}
            files={resourcesState.resourceFiles || []}
            onFolderClick={handleResourceExplorerFolderChange}
            onBackClick={handleResourceExplorerBackNavigation}
            onFileClick={handleResourceExplorerFileClick}
            onSelectedFileDelete={handleResourceExplorerSelectedFileDelete}
            hiddenFiles={hiddenFiles}
          />
        </div>
        <Box
          style={{ padding: '10px 15px', backgroundColor: 'background.paper' }}
        >
          <Button
            fullWidth
            variant="contained"
            color="primary"
            disabled={selectedResources.length === 0}
            onClick={handleSubmit}
          >
            Add
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};

export default AddNewResourceModal;
